import type { SxProps, Theme } from '@mui/material/styles';
import { LAYOUT_CONSTANTS } from '../../utils/layout-constants';

export const BOTTOM_SNACKBAR_STACK_START_INDEX = 0;
export const BOTTOM_SNACKBAR_STACK_STEP_PX = 64;
export const BOTTOM_SNACKBAR_STACK_GAP_PX = 8;

export function getBottomSnackbarStackSlots(height: number, minimumSlots = 1): number {
  const measuredHeight = Math.max(0, height);
  const measuredSlots = measuredHeight === 0 ? 0 : Math.ceil((measuredHeight + BOTTOM_SNACKBAR_STACK_GAP_PX) / BOTTOM_SNACKBAR_STACK_STEP_PX);

  return Math.max(minimumSlots, measuredSlots);
}

/**
 * Caps bottom snackbar content so measured collision checks have a predictable
 * upper bound while still allowing shorter messages to use their natural width.
 */
export const BOTTOM_SNACKBAR_MAX_CONTENT_WIDTH_PX = 480;

export const BOTTOM_SNACKBAR_DESKTOP_EDGE_INSET_PX = 24;
export const BOTTOM_SNACKBAR_DESKTOP_GAP_PX = 24;

export type BottomSnackbarPlacement = 'center' | 'right' | 'stacked';

export function getBottomSnackbarPlacement({
  desktopLayoutMinWidth,
  leftStackWidth,
  snackbarWidth,
  viewportWidth,
}: {
  desktopLayoutMinWidth: number;
  leftStackWidth: number;
  snackbarWidth: number;
  viewportWidth: number;
}): BottomSnackbarPlacement {
  if (leftStackWidth <= 0) {
    return 'center';
  }

  if (viewportWidth < desktopLayoutMinWidth) {
    return 'stacked';
  }

  const leftStackRight = BOTTOM_SNACKBAR_DESKTOP_EDGE_INSET_PX + leftStackWidth;
  const centeredSnackbarLeft = (viewportWidth - snackbarWidth) / 2;

  if (centeredSnackbarLeft - leftStackRight >= BOTTOM_SNACKBAR_DESKTOP_GAP_PX) {
    return 'center';
  }

  const rightSnackbarLeft = viewportWidth - BOTTOM_SNACKBAR_DESKTOP_EDGE_INSET_PX - snackbarWidth;

  if (rightSnackbarLeft - leftStackRight >= BOTTOM_SNACKBAR_DESKTOP_GAP_PX) {
    return 'right';
  }

  return 'stacked';
}

export const constrainedBottomSnackbarContentSx = {
  maxWidth: BOTTOM_SNACKBAR_MAX_CONTENT_WIDTH_PX,
  overflowWrap: 'anywhere',
} as const;

export const fixedWidthBottomSnackbarRootSx: SxProps<Theme> = (theme) => ({
  [theme.breakpoints.up('sm')]: {
    width: BOTTOM_SNACKBAR_MAX_CONTENT_WIDTH_PX,
  },
});

export const fullWidthConstrainedBottomSnackbarContentSx = {
  ...constrainedBottomSnackbarContentSx,
  width: '100%',
} as const;

export const getResetOrientationAcknowledgementStackIndex = (): number => BOTTOM_SNACKBAR_STACK_START_INDEX;

export const getViewerReadOnlyModeStackIndex = (): number => BOTTOM_SNACKBAR_STACK_START_INDEX;

export const centeredPlainSnackbarContentSx: SxProps<Theme> = {
  justifyContent: 'center',
  textAlign: 'center',
  '& .MuiSnackbarContent-message': {
    textAlign: 'center',
    width: '100%',
  },
};

export const defaultSnackbarContentSx: SxProps<Theme> = (theme) => ({
  backgroundColor: theme.vars?.palette?.background?.paper,
  color: theme.vars?.palette?.text?.primary,
});

const bottomSnackbarStackSx =
  (index: number): SxProps<Theme> =>
  (theme) => ({
    bottom: `calc(${theme.spacing(3)} + ${index * BOTTOM_SNACKBAR_STACK_STEP_PX}px + ${LAYOUT_CONSTANTS.BOTTOM_CONTROL_BAR_HEIGHT}px) !important`,
    [theme.breakpoints.down('sm')]: {
      bottom: `calc(${theme.spacing(2)} + ${index * BOTTOM_SNACKBAR_STACK_STEP_PX}px + ${LAYOUT_CONSTANTS.BOTTOM_CONTROL_BAR_HEIGHT}px) !important`,
    },
  });

export const bottomCenterSnackbarStackSx = bottomSnackbarStackSx;

export const bottomLeftSnackbarStackSx = bottomSnackbarStackSx;

// MUI resolves sx array entries left-to-right, so pass base styles before call-site overrides.
export const mergeSx = (...styles: Array<SxProps<Theme> | undefined>): SxProps<Theme> =>
  styles.flatMap((style) => {
    if (!style) {
      return [];
    }

    return Array.isArray(style) ? style : [style];
  }) as SxProps<Theme>;
